import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useApp, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { InfoCard } from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { stringify as yamlStringify, parseAllDocuments } from 'yaml';
import {
  Grid, TextField, MenuItem, Button, Box, Typography, IconButton,
  FormControlLabel, Checkbox, Chip, Accordion, AccordionSummary, AccordionDetails, Alert, Autocomplete,
} from '@mui/material';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';

const COMPONENT_TYPES = ['service', 'website', 'library', 'documentation', 'tool', 'other'];
const LIFECYCLES = ['experimental', 'production', 'deprecated'];

type KV = { key: string; value: string };
type LinkRow = { url: string; title: string; icon: string; type: string };
type FormState = {
  name: string; namespace: string; title: string; description: string;
  type: string; lifecycle: string; owner: string; system: string; subcomponentOf: string;
  tags: string[];
  annotations: KV[]; labels: KV[]; links: LinkRow[];
  providesApis: string[]; consumesApis: string[]; dependsOn: string[];
};

const objToKV = (o?: Record<string, string>): KV[] =>
  Object.entries(o ?? {}).map(([key, value]) => ({ key, value: String(value) }));
const kvToObj = (rows: KV[]): Record<string, string> => {
  const o: Record<string, string> = {};
  rows.forEach(r => { if (r.key.trim()) o[r.key.trim()] = r.value; });
  return o;
};

export const seedForm = (entity: Entity): FormState => {
  const m = entity.metadata ?? ({} as any);
  const s = (entity.spec ?? {}) as any;
  return {
    name: m.name ?? '',
    namespace: m.namespace && m.namespace !== 'default' ? m.namespace : '',
    title: (m.title as string) ?? '',
    description: m.description ?? '',
    type: s.type ?? 'other',
    lifecycle: s.lifecycle ?? 'experimental',
    owner: s.owner ?? 'group:default/online',
    system: s.system ?? '',
    subcomponentOf: s.subcomponentOf ?? '',
    tags: (m.tags ?? []) as string[],
    annotations: objToKV(m.annotations),
    labels: objToKV(m.labels),
    links: ((m.links ?? []) as any[]).map(l => ({ url: l.url ?? '', title: l.title ?? '', icon: l.icon ?? '', type: l.type ?? '' })),
    providesApis: (s.providesApis ?? []) as string[],
    consumesApis: (s.consumesApis ?? []) as string[],
    dependsOn: (s.dependsOn ?? []) as string[],
  };
};

export const buildEntity = (f: FormState): Record<string, any> => {
  const metadata: Record<string, any> = { name: f.name };
  if (f.namespace) metadata.namespace = f.namespace;
  if (f.title) metadata.title = f.title;
  if (f.description) metadata.description = f.description;
  const ann = kvToObj(f.annotations); if (Object.keys(ann).length) metadata.annotations = ann;
  const lab = kvToObj(f.labels); if (Object.keys(lab).length) metadata.labels = lab;
  const tags = f.tags.map(t => t.trim()).filter(Boolean); if (tags.length) metadata.tags = tags;
  const links = f.links.filter(l => l.url.trim()).map(l => {
    const o: Record<string, string> = { url: l.url.trim() };
    if (l.title) o.title = l.title; if (l.icon) o.icon = l.icon; if (l.type) o.type = l.type;
    return o;
  });
  if (links.length) metadata.links = links;

  const spec: Record<string, any> = { type: f.type, lifecycle: f.lifecycle, owner: f.owner };
  if (f.system) spec.system = f.system;
  if (f.subcomponentOf) spec.subcomponentOf = f.subcomponentOf;
  const clean = (xs: string[]) => xs.map(x => x.trim()).filter(Boolean);
  const pa = clean(f.providesApis); if (pa.length) spec.providesApis = pa;
  const ca = clean(f.consumesApis); if (ca.length) spec.consumesApis = ca;
  const deps = clean(f.dependsOn); if (deps.length) spec.dependsOn = deps;

  return { apiVersion: 'backstage.io/v1alpha1', kind: 'Component', metadata, spec };
};

/** Reconcile a desired Component object into an existing yaml Document, preserving comments
 *  on untouched nodes + sibling docs. Updates changed keys, removes dropped ones. */
const reconcileDoc = (doc: any, obj: Record<string, any>) => {
  doc.set('apiVersion', obj.apiVersion);
  doc.set('kind', obj.kind);
  for (const top of ['metadata', 'spec'] as const) {
    const desired: Record<string, any> = obj[top] ?? {};
    const cur: any = doc.get(top);
    for (const [k, v] of Object.entries(desired)) doc.setIn([top, k], v);
    if (cur && Array.isArray(cur.items)) {
      const keep = new Set(Object.keys(desired));
      for (const it of [...cur.items]) {
        const k = String((it.key as any)?.value ?? it.key);
        if (!keep.has(k)) doc.deleteIn([top, k]);
      }
    }
  }
};

/** Merge the form into the original raw file (comments + sibling docs preserved). */
const mergeIntoRaw = (rawText: string, f: FormState): string => {
  try {
    const docs = parseAllDocuments(rawText);
    const compDoc = docs.find(d => (d.get('kind') as unknown) === 'Component') ?? docs[0];
    if (!compDoc) return yamlStringify(buildEntity(f));
    reconcileDoc(compDoc, buildEntity(f));
    return docs.map(d => String(d).replace(/^---\s*\n/, '').trimEnd()).join('\n---\n') + '\n';
  } catch {
    return yamlStringify(buildEntity(f));
  }
};

/** A labelled form section. The fields use a CSS-grid (FieldGrid) rather than MUI <Grid>,
 *  because <Grid container spacing> applies a negative top margin that bleeds upward and
 *  collides the first row's floating labels with this header. CSS-grid gap has no such bleed. */
const Section = ({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) => (
  <Box mb={4}>
    <Typography
      variant="overline"
      component="div"
      sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1, lineHeight: 1.6 }}
    >
      {title}
    </Typography>
    {hint && <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.25 }}>{hint}</Typography>}
    {children}
  </Box>
);

/** Two-column responsive field grid (single column on xs). Uses CSS grid gap, not MUI Grid. */
const FieldGrid = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2, rowGap: 2.5, mt: 2.5 }}>
    {children}
  </Box>
);
/** Span a field across both columns of FieldGrid. */
const Full = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ gridColumn: '1 / -1' }}>{children}</Box>
);

/** Quiet header used inside an Accordion summary (count badge, same vocabulary as Section). */
const SectionHead = ({ title }: { title: string }) => (
  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
    {title}
  </Typography>
);

const collapseSx = { bgcolor: 'transparent', '&:before': { display: 'none' } } as const;

const KVEditor = ({ rows, onChange, noun }: { rows: KV[]; onChange: (r: KV[]) => void; noun: string }) => (
  <Box>
    {rows.length === 0 && (
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>No {noun}s yet.</Typography>
    )}
    {rows.map((r, i) => (
      <Box key={i} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.5fr) 36px', gap: 1, mb: 1, alignItems: 'center' }}>
        <TextField size="small" placeholder="key" value={r.key}
          onChange={e => onChange(rows.map((x, j) => j === i ? { ...x, key: e.target.value } : x))} />
        <TextField size="small" placeholder="value" value={r.value}
          InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          onChange={e => onChange(rows.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} />
        <IconButton aria-label={`remove ${noun} ${i + 1}`} size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
          <ClearIcon fontSize="small" />
        </IconButton>
      </Box>
    ))}
    <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => onChange([...rows, { key: '', value: '' }])}>
      Add {noun}
    </Button>
  </Box>
);

const LINK_COLS = 'minmax(0,2.2fr) minmax(0,1.6fr) minmax(0,1fr) minmax(0,1fr) 36px';

// The app's default registered link icons. catalog-info `icon` must match a registered name,
// or it renders nothing; offering them (with a preview) avoids guesswork. freeSolo keeps custom
// app-registered icons usable.
const LINK_ICONS = ['github', 'docs', 'dashboard', 'chat', 'email', 'group', 'help', 'user', 'warning', 'search', 'catalog', 'techdocs', 'scaffolder', 'star'];

/** Single-select of a registered icon name, with a live preview of each icon in the list. */
const LinkIconPicker = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const app = useApp();
  const Selected = value ? app.getSystemIcon(value) : undefined;
  return (
    <Autocomplete
      freeSolo fullWidth size="small" forcePopupIcon autoHighlight openOnFocus
      options={LINK_ICONS} value={value || null}
      onChange={(_, v) => onChange((v as string | null) ?? '')}
      onInputChange={(_, v, reason) => { if (reason === 'input' || reason === 'clear') onChange(v); }}
      renderOption={(props, name) => {
        const { key, ...rest } = props as any;
        const Icon = app.getSystemIcon(name);
        return (
          <Box component="li" key={key ?? name} {...rest} sx={{ ...optionSx, display: 'flex', gap: 1, alignItems: 'center' }}>
            {Icon ? <Icon fontSize="small" /> : <Box sx={{ width: 20 }} />}
            <span>{name}</span>
          </Box>
        );
      }}
      renderInput={p => (
        <TextField {...p} placeholder="github"
          InputProps={{ ...p.InputProps, startAdornment: Selected ? <Box component="span" sx={{ display: 'inline-flex', mr: 0.5, opacity: 0.85 }}><Selected fontSize="small" /></Box> : p.InputProps?.startAdornment }} />
      )}
    />
  );
};
const LinkEditor = ({ rows, onChange }: { rows: LinkRow[]; onChange: (r: LinkRow[]) => void }) => {
  const upd = (i: number, patch: Partial<LinkRow>) => onChange(rows.map((x, j) => j === i ? { ...x, ...patch } : x));
  return (
    <Box>
      {rows.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>No links yet.</Typography>
      )}
      {rows.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: LINK_COLS, gap: 1, mb: 0.5, px: 0.25 }}>
          {['url (required)', 'title', 'icon', 'type', ''].map((c, k) => (
            <Typography key={k} variant="caption" sx={{ color: 'text.secondary' }}>{c}</Typography>
          ))}
        </Box>
      )}
      {rows.map((r, i) => (
        <Box key={i} sx={{ display: 'grid', gridTemplateColumns: LINK_COLS, gap: 1, mb: 1, alignItems: 'center' }}>
          <TextField size="small" placeholder="https://…" value={r.url}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            onChange={e => upd(i, { url: e.target.value })} />
          <TextField size="small" placeholder="GitHub" value={r.title} onChange={e => upd(i, { title: e.target.value })} />
          <LinkIconPicker value={r.icon} onChange={v => upd(i, { icon: v })} />
          <TextField size="small" placeholder="docs" value={r.type} onChange={e => upd(i, { type: e.target.value })} />
          <IconButton aria-label={`remove link ${i + 1}`} size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => onChange([...rows, { url: '', title: '', icon: '', type: '' }])}>
        Add link
      </Button>
    </Box>
  );
};

const entityRef = (e: Entity) => `${e.kind.toLowerCase()}:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`;

// Visible hover / keyboard-focus / selected highlight for dropdown options (the dark theme
// leaves them transparent). Rendered per-option because this MUI version lacks a listbox slot.
const optionSx = {
  '&:hover': { backgroundColor: 'rgba(112, 55, 175, 0.16)' },
  '&.Mui-focused': { backgroundColor: 'rgba(112, 55, 175, 0.22) !important' },
  '&[aria-selected="true"]': { backgroundColor: 'rgba(112, 55, 175, 0.32) !important' },
};
const renderHighlightedOption = (props: any, option: string) => {
  const { key, ...rest } = props;
  return (
    <Box component="li" key={key ?? option} {...rest} sx={optionSx}>{option}</Box>
  );
};

// The Backstage theme sets the Autocomplete input root to flex-wrap: nowrap, so multi-select
// chips overflow the field instead of wrapping. Force wrap so chips flow onto new lines.
const chipWrapSx = {
  '& [class*="inputRoot"]': {
    flexWrap: 'wrap',
    rowGap: '4px',
    paddingTop: '7px',
    paddingBottom: '7px',
  },
  // The v5- class prefix breaks MUI's default slimming of the Autocomplete text input, so it
  // keeps the 16.5px outlined padding (56px tall) and takes its own row. Slim it so it shares
  // the chip row and the field stays compact.
  '& input[class*="Autocomplete-input"]': {
    padding: '2.5px 4px !important',
    minWidth: '50px !important',
    width: '0 !important',
    flexGrow: 1,
  },
  // The theme zeroes the chip delete-icon margins, so the X sits flush against the edge.
  // Restore MUI's default breathing room (matches the scaffolder template chips).
  '& [class*="MuiChip-deleteIcon"]': { marginLeft: '-4px', marginRight: '6px' },
} as const;

/** Shared: catalog entity refs of the given kinds, as autocomplete options. */
const useEntityRefOptions = (kinds: string[]) => {
  const catalogApi = useApi(catalogApiRef);
  const [options, setOptions] = useState<string[]>([]);
  const key = kinds.join(',');
  useEffect(() => {
    let active = true;
    catalogApi
      .getEntities({ filter: kinds.map(k => ({ kind: k })), fields: ['kind', 'metadata.name', 'metadata.namespace'] })
      .then(r => { if (active) setOptions(r.items.map(entityRef).sort()); })
      .catch(() => { if (active) setOptions([]); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, key]);
  return options;
};

/** Shared: distinct values of a catalog facet (e.g. metadata.namespace / metadata.tags). */
const useFacetOptions = (facet: string) => {
  const catalogApi = useApi(catalogApiRef);
  const [options, setOptions] = useState<string[]>([]);
  useEffect(() => {
    let active = true;
    catalogApi
      .getEntityFacets({ facets: [facet] })
      .then(r => { if (active) setOptions((r.facets[facet] ?? []).map(x => x.value).sort()); })
      .catch(() => { if (active) setOptions([]); });
    return () => { active = false; };
  }, [catalogApi, facet]);
  return options;
};

/** Multi-select of catalog entity refs as removable chips (scaffolder MultiEntityPicker feel). */
const EntityRefsField = ({ label, helper, kinds, value, onChange }: {
  label: string; helper?: string; kinds: string[]; value: string[]; onChange: (v: string[]) => void;
}) => {
  const options = useEntityRefOptions(kinds);
  return (
    <Autocomplete
      multiple freeSolo fullWidth forcePopupIcon autoHighlight openOnFocus sx={chipWrapSx}
      renderOption={renderHighlightedOption} options={options} value={value}
      onChange={(_, v) => onChange(v as string[])}
      filterSelectedOptions
      renderInput={p => (
        <TextField {...p} label={label} helperText={helper}
          placeholder={value.length ? '' : 'Search the catalog…'}
          InputLabelProps={{ ...p.InputLabelProps, shrink: true }} />
      )}
    />
  );
};

/** Single-select editable dropdown over catalog entity refs (owner, system, subcomponentOf). */
const EntityRefField = ({ label, helper, kinds, value, onChange, required }: {
  label: string; helper?: string; kinds: string[]; value: string; onChange: (v: string) => void; required?: boolean;
}) => {
  const options = useEntityRefOptions(kinds);
  return (
    <Autocomplete
      freeSolo fullWidth forcePopupIcon autoHighlight openOnFocus
      renderOption={renderHighlightedOption} options={options} value={value || null}
      onChange={(_, v) => onChange((v as string | null) ?? '')}
      onInputChange={(_, v, reason) => { if (reason === 'input' || reason === 'clear') onChange(v); }}
      renderInput={p => (
        <TextField {...p} required={required} label={label} helperText={helper}
          placeholder="Search the catalog…"
          InputLabelProps={{ ...p.InputLabelProps, shrink: true }} />
      )}
    />
  );
};

/** Single-select editable dropdown over a catalog facet (e.g. namespace) for consistency. */
const FacetField = ({ label, helper, facet, value, onChange }: {
  label: string; helper?: string; facet: string; value: string; onChange: (v: string) => void;
}) => {
  const options = useFacetOptions(facet);
  return (
    <Autocomplete
      freeSolo fullWidth forcePopupIcon autoHighlight openOnFocus
      renderOption={renderHighlightedOption} options={options} value={value || null}
      onChange={(_, v) => onChange((v as string | null) ?? '')}
      onInputChange={(_, v, reason) => { if (reason === 'input' || reason === 'clear') onChange(v); }}
      renderInput={p => (
        <TextField {...p} label={label} helperText={helper}
          placeholder="Search or type…"
          InputLabelProps={{ ...p.InputLabelProps, shrink: true }} />
      )}
    />
  );
};

/** Tag chips with suggestions from existing catalog tags (facet); freeSolo for new tags. */
const TagsField = ({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) => {
  const options = useFacetOptions('metadata.tags');
  return (
    <Autocomplete
      multiple freeSolo fullWidth openOnFocus sx={chipWrapSx} renderOption={renderHighlightedOption} options={options} value={value}
      onChange={(_, v) => onChange(v as string[])}
      filterSelectedOptions
      renderInput={p => (
        <TextField {...p} label="tags" helperText="type and press Enter to add"
          placeholder={value.length ? '' : 'Add a tag…'}
          InputLabelProps={{ ...p.InputLabelProps, shrink: true }} />
      )}
    />
  );
};

export const CatalogInfoEditor = ({ entity }: { entity: Entity }) => {
  const scaffolderApi = useApi(scaffolderApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(() => seedForm(entity));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [originalRaw, setOriginalRaw] = useState<string | undefined>();
  const [fileStatus, setFileStatus] = useState<'loading' | 'existing' | 'new'>('loading');
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState(true);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(p => ({ ...p, [k]: v }));

  // Fetch the committed catalog-info.yaml (if any) so edits round-trip through the yaml Document
  // API (comments + sibling docs preserved). Re-seed the form from the file when it exists.
  useEffect(() => {
    const slugAnn = (entity.metadata.annotations ?? {})['github.com/project-slug'] ?? '';
    const [owner, repo] = slugAnn.split('/');
    if (!owner || !repo) { setFileStatus('new'); return undefined; }
    let active = true;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('catalog-info-reader');
        const resp = await fetchApi.fetch(
          `${base}/file?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}`,
        );
        const data = await resp.json();
        if (!active) return;
        if (data.found && typeof data.content === 'string') {
          setOriginalRaw(data.content);
          setFileStatus('existing');
          try {
            const docs = parseAllDocuments(data.content);
            const comp = docs.find(d => (d.get('kind') as unknown) === 'Component') ?? docs[0];
            if (comp) setForm(seedForm(comp.toJSON() as Entity));
          } catch { /* keep entity-seeded form */ }
        } else {
          setFileStatus('new');
        }
      } catch {
        if (active) setFileStatus('new');
      }
    })();
    return () => { active = false; };
  }, [entity, discoveryApi, fetchApi]);

  const yamlText = useMemo(() => {
    try {
      return originalRaw ? mergeIntoRaw(originalRaw, form) : yamlStringify(buildEntity(form));
    } catch (e: any) { return `# render error: ${e?.message}`; }
  }, [form, originalRaw]);

  // PR target is the entity's actual repo (stable), not the editable form: an existing
  // catalog-info.yaml usually lacks the github.com/project-slug annotation (it is injected at
  // catalog-ingest time), so deriving from the form would empty the slug and disable Create-PR.
  const slug = useMemo(
    () => (entity.metadata.annotations ?? {})['github.com/project-slug'] ?? '',
    [entity],
  );

  const onCreatePr = async () => {
    setError(undefined);
    const [owner, repo] = slug.split('/');
    if (!owner || !repo) { setError('Missing/invalid github.com/project-slug annotation (need <org>/<repo>).'); return; }
    setSubmitting(true);
    try {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef: 'template:default/write-catalog-info',
        values: {
          repoUrl: `github.com?repo=${repo}&owner=${owner}`,
          content: yamlText,
          branchName: `backstage-catalog-info-${form.name}`,
          prTitle: `[Backstage] catalog-info.yaml for ${form.name}`,
          draft,
        },
      });
      navigate(`/create/tasks/${taskId}`);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setSubmitting(false);
    }
  };

  const onCopy = async () => {
    try { await navigator.clipboard.writeText(yamlText); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  const modeBanner =
    fileStatus === 'existing' ? (
      <Alert severity="info" data-testid="file-status" sx={{ mb: 2.5 }}>
        <strong>Editing the existing catalog-info.yaml.</strong> Your comments, key order, and any
        other documents in the file are preserved when you open the PR.
      </Alert>
    ) : fileStatus === 'new' ? (
      <Alert severity="warning" data-testid="file-status" sx={{ mb: 2.5 }}>
        <strong>No catalog-info.yaml yet.</strong> Opening the PR creates a new one for this repo.
      </Alert>
    ) : (
      <Alert severity="info" icon={false} data-testid="file-status" sx={{ mb: 2.5 }}>
        Checking for an existing catalog-info.yaml…
      </Alert>
    );

  return (
    <Box>
      {modeBanner}
      <Grid container spacing={3}>
      {/* ---- Form ---- */}
      <Grid item xs={12} md={7}>
        <InfoCard title="Edit catalog-info.yaml" subheader="Fields map 1:1 to the descriptor. Required: type, lifecycle, owner.">
          <Section title="Identity">
            <FieldGrid>
              <TextField fullWidth label="name" value={form.name} disabled helperText="entity name (from repo)" />
              <FacetField label="namespace" facet="metadata.namespace" value={form.namespace} onChange={v => set('namespace', v)} helper="blank = default" />
              <TextField fullWidth label="title" value={form.title} onChange={e => set('title', e.target.value)} helperText="optional display name" />
              <TagsField value={form.tags} onChange={v => set('tags', v)} />
              <Full><TextField fullWidth multiline minRows={1} maxRows={6} label="description" value={form.description} onChange={e => set('description', e.target.value)} placeholder="one line about what this component is" InputLabelProps={{ shrink: true }} /></Full>
            </FieldGrid>
          </Section>

          <Section title="Classification">
            <FieldGrid>
              <TextField select fullWidth required label="type" value={form.type} onChange={e => set('type', e.target.value)}>{COMPONENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField>
              <TextField select fullWidth required label="lifecycle" value={form.lifecycle} onChange={e => set('lifecycle', e.target.value)}>{LIFECYCLES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField>
              <EntityRefField label="owner" required kinds={['Group', 'User']} value={form.owner} onChange={v => set('owner', v)} helper="e.g. group:default/online" />
              <EntityRefField label="system" kinds={['System']} value={form.system} onChange={v => set('system', v)} helper="e.g. identity" />
            </FieldGrid>
          </Section>

          <Section title="Relations" hint="Pick catalog entities; type to filter. A ref not yet in the catalog can still be entered.">
            <FieldGrid>
              <Full><EntityRefField label="Subcomponent of" kinds={['Component']} value={form.subcomponentOf} onChange={v => set('subcomponentOf', v)} helper="the parent component, if this is part of a larger one" /></Full>
              <Full><EntityRefsField label="Depends on" kinds={['Component', 'Resource', 'API']} value={form.dependsOn} onChange={v => set('dependsOn', v)} helper="components, resources or APIs this depends on" /></Full>
              <Full><EntityRefsField label="Provides APIs" kinds={['API']} value={form.providesApis} onChange={v => set('providesApis', v)} helper="APIs this component exposes" /></Full>
              <Full><EntityRefsField label="Consumes APIs" kinds={['API']} value={form.consumesApis} onChange={v => set('consumesApis', v)} helper="APIs this component calls" /></Full>
            </FieldGrid>
          </Section>

          <Accordion disableGutters elevation={0} sx={collapseSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <SectionHead title={`Links (${form.links.length})`} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, display: 'block' }}>
              <LinkEditor rows={form.links} onChange={r => set('links', r)} />
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0} sx={collapseSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <SectionHead title={`Annotations (${form.annotations.length})`} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, display: 'block' }}>
              <KVEditor rows={form.annotations} onChange={r => set('annotations', r)} noun="annotation" />
            </AccordionDetails>
          </Accordion>

          <Accordion disableGutters elevation={0} sx={collapseSx}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
              <SectionHead title={`Labels (${form.labels.length})`} />
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, display: 'block' }}>
              <KVEditor rows={form.labels} onChange={r => set('labels', r)} noun="label" />
            </AccordionDetails>
          </Accordion>
        </InfoCard>
      </Grid>

      {/* ---- Preview ---- */}
      <Grid item xs={12} md={5}>
        <Box>
          <InfoCard title="catalog-info.yaml">
            <Box mb={1.5} display="flex" gap={1} flexWrap="wrap" alignItems="center">
              {slug
                ? <Chip label={`PR target · ${slug}`} color="primary" size="small" />
                : <Chip label="set github.com/project-slug to open a PR" size="small" color="warning" variant="outlined" />}
              <Box flexGrow={1} />
              <Button size="small" onClick={onCopy} data-testid="copy-yaml">{copied ? 'Copied' : 'Copy'}</Button>
            </Box>
            <Box
              component="pre"
              data-testid="yaml-preview"
              sx={{
                m: 0, p: 1.5, borderRadius: 1, maxHeight: '60vh', overflow: 'auto',
                fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.55,
                whiteSpace: 'pre', bgcolor: 'action.hover',
                border: theme => `1px solid ${theme.palette.divider}`,
              }}
            >
              {yamlText}
            </Box>
            {error && <Typography color="error" variant="body2" data-testid="editor-error" sx={{ mt: 1.5 }}>{error}</Typography>}
            <Box mt={2} display="flex" alignItems="center" justifyContent="space-between">
              <Button variant="contained" color="primary" disabled={submitting || !slug} onClick={onCreatePr} data-testid="create-pr">
                {submitting ? 'Opening PR…' : 'Create Pull Request'}
              </Button>
              <FormControlLabel
                control={<Checkbox checked={draft} onChange={e => setDraft(e.target.checked)} size="small" color="primary" />}
                label="Open as a draft PR"
              />
            </Box>
          </InfoCard>
        </Box>
      </Grid>
      </Grid>
    </Box>
  );
};
